import { apiClient } from './client'
import type { Client, ClientCategory, Request } from './types'

interface ClientsQuery {
  search?: string
  category?: ClientCategory
  page?: number
  limit?: number
}

interface ClientsResponse {
  items: Client[]
  total: number
  page: number
  limit: number
}

interface ClientDetail extends Client {
  requests?: Request[]
}

export async function getClients(query: ClientsQuery = {}): Promise<ClientsResponse> {
  const res = await apiClient.get<{ success: true; data: ClientsResponse }>('/clients', {
    params: query,
  })
  return res.data.data
}

export async function getClient(id: string): Promise<ClientDetail> {
  const res = await apiClient.get<{ success: true; data: ClientDetail }>(`/clients/${id}`)
  return res.data.data
}

export async function createClient(data: {
  name: string
  code?: string
  category: ClientCategory
  managerId?: string
  notes?: string
  inn?: string | null
  city?: string | null
  segment?: string | null
  /** Сохранить, хотя такой ИНН уже есть (после ответа 409 DUPLICATE_INN). */
  confirmDuplicateInn?: boolean
}): Promise<Client> {
  const res = await apiClient.post<{ success: true; data: Client }>('/clients', data)
  return res.data.data
}

export async function updateClient(
  id: string,
  data: Partial<{
    name: string
    code: string
    category: ClientCategory
    managerId: string
    notes: string
    inn: string | null
    city: string | null
    segment: string | null
    /** Сохранить, хотя такой ИНН уже есть (после ответа 409 DUPLICATE_INN). */
    confirmDuplicateInn: boolean
  }>,
): Promise<Client> {
  const res = await apiClient.patch<{ success: true; data: Client }>(`/clients/${id}`, data)
  return res.data.data
}

export async function deleteClient(id: string): Promise<void> {
  await apiClient.delete(`/clients/${id}`)
}

export interface AssignableOwner {
  id: string
  name: string | null
  email: string
  role: 'ADMIN' | 'MANAGER' | 'ROP'
}

/** Кому актор (ADMIN/ROP) может передать клиента — для дропдауна смены владельца. */
export async function getAssignableOwners(): Promise<AssignableOwner[]> {
  const res = await apiClient.get<{ success: true; data: AssignableOwner[] }>(
    '/clients/assignable-owners',
  )
  return res.data.data
}

export interface ReassignOwnerResult {
  clientId: string
  oldOwnerId: string
  newOwnerId: string
  transferredRequestsCount: number
}

/** Смена владельца клиента (ADMIN/ROP). */
export async function reassignClientOwner(
  id: string,
  body: { newOwnerId: string; transferActiveRequests: boolean },
): Promise<ReassignOwnerResult> {
  const res = await apiClient.patch<{ success: true; data: ReassignOwnerResult }>(
    `/clients/${id}/owner`,
    body,
  )
  return res.data.data
}

export async function getClientCities(): Promise<string[]> {
  const res = await apiClient.get<{ success: true; data: string[] }>('/clients/cities')
  return res.data.data
}

export interface InnCheckResult {
  id: string
  name: string
  manager: { name: string | null; email: string }
}

export async function checkClientInn(inn: string): Promise<InnCheckResult | null> {
  const res = await apiClient.get<{ success: true; data: InnCheckResult | null }>(
    '/clients/check-inn',
    { params: { inn } },
  )
  return res.data.data
}

export async function checkClientCode(code: string): Promise<InnCheckResult | null> {
  const res = await apiClient.get<{ success: true; data: InnCheckResult | null }>(
    '/clients/check-code',
    { params: { code } },
  )
  return res.data.data
}
